import { Routes, Route, BrowserRouter, Navigate, useLocation } from "react-router-dom";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { ColorModeContext, useMode } from "./theme";
import { useUserContext } from "./contexts/userContext";
import { ConfirmProvider } from "material-ui-confirm";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import Topbar from "./scenes/global/Topbar";
import Sidebar from "./scenes/global/Sidebar";
import Dashboard from "./scenes/dashboard";
import SaleOrders from "./scenes/saleOrders/SaleOrders";
import WorkOrders from "./scenes/workOrders/WorkOrders";
import WorkFlows from "./scenes/workFlows/WorkFlows";
import Wizard from "./scenes/wizard/Wizard";
import Events from "./scenes/eventsPage/events";
import Logs from "./scenes/logs/Logs.jsx";
import Administration from "./components/Administration/Administration.jsx";
import Flow from "./scenes/dagDesignerTool/DagDesignerTool";
import DagTemplates from "./scenes/dagDesignerTool/DagTemplates";
import OperationalDataView from "./scenes/dataView/OperationalDataView.jsx";
import BusinessDataView from "./scenes/dataView/BusinessDataView.jsx";
import CoverageDrilldown from "./scenes/coverageDrilldown/CoverageDrilldown.jsx";
import { buildPermissions } from "./auth/permissions";

function AppRoutes() {
	const location = useLocation();
	const isCoverageDrilldown = location.pathname.startsWith("/coverage-drilldown");

	const userContext = useUserContext();
	const user = userContext?.getUser ? userContext.getUser() : null;

	const hasRole = (role) => !!user && typeof user.hasRole === "function" && user.hasRole(role);
	const { canViewBusinessDataView, canViewOperationalDataView, defaultDataViewPath } =
		buildPermissions(hasRole);

	return (
		<div className="app">
			{!isCoverageDrilldown && <Sidebar />}
			<main
				className="content"
				style={isCoverageDrilldown ? { width: "100%" } : undefined}
			>
				{!isCoverageDrilldown && <Topbar />}
				<Routes>
					<Route path="/" element={<Dashboard />} />
					<Route path="/salesorders" element={<SaleOrders />} />
					<Route path="/workorders" element={<WorkOrders />} />
					<Route path="/workflows" element={<WorkFlows />} />
					<Route path="/wizard" element={<Wizard />} />
					<Route path="/events" element={<Events />} />
					<Route path="/logs" element={<Logs />} />
					<Route path="/administration" element={<Administration />} />
					<Route
						path="/operational-dataview"
						element={
							canViewOperationalDataView ? (
								<OperationalDataView />
							) : (
								<Navigate to="/" replace />
							)
						}
					/>
					<Route
						path="/business-dataview"
						element={
							canViewBusinessDataView ? (
								<BusinessDataView />
							) : (
								<Navigate to="/" replace />
							)
						}
					/>
					<Route
						path="/dataview"
						element={
							defaultDataViewPath === "/" ? (
								<Navigate to="/" replace />
							) : (
								<Navigate to={defaultDataViewPath} replace />
							)
						}
					/>
					<Route path="/dag_templates" element={<DagTemplates />} />
					<Route path="/dag_templates/:dagName" element={<Flow />} />
					<Route path="/coverage-drilldown" element={<CoverageDrilldown />} />
				</Routes>
			</main>
		</div>
	);
}

function App() {
	const [theme, colorMode] = useMode();

	return (
		<ColorModeContext.Provider value={colorMode}>
			<ThemeProvider theme={theme}>
				<CssBaseline />
				<ConfirmProvider>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<BrowserRouter>
							<AppRoutes />
						</BrowserRouter>
					</LocalizationProvider>
				</ConfirmProvider>
			</ThemeProvider>
		</ColorModeContext.Provider>
	);
}

export default App;
